import { Progress } from "@/components/ui/progress"

const STEPS = [
  { key: "style", label: "Style" },
  { key: "script", label: "Script" },
  { key: "audio", label: "Audio" },
  { key: "storyboard", label: "Storyboard" },
  { key: "render", label: "Render" },
] as const

export type WizardStep = (typeof STEPS)[number]["key"]

export default function WizardTimeline({
  currentStep,
  onStepClick,
}: {
  currentStep: WizardStep
  onStepClick: (step: WizardStep) => void
}) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep)
  const progressPercent = (currentIndex / (STEPS.length - 1)) * 100

  return (
    <>
      {/* Desktop timeline */}
      <nav className="mb-8 hidden lg:block" aria-label="Wizard progress">
        <div className="relative grid grid-cols-5">
          {/* Connector line — spans centre of first column to centre of last */}
          <div
            className="pointer-events-none absolute top-4 -translate-y-px"
            style={{ left: "calc(100% / 10)", right: "calc(100% / 10)" }}
          >
            {/* Track */}
            <div className="h-px w-full bg-stone" />
            {/* Fill */}
            <div
              className="absolute inset-y-0 left-0 h-px bg-stone transition-all duration-300"
              style={{
                width: currentIndex === 0 ? "0%" : `${(currentIndex / (STEPS.length - 1)) * 100}%`,
                background: "linear-gradient(90deg, #E8E4DF, #C41E2A)",
              }}
            />
          </div>

          {STEPS.map((step, i) => {
            const isCompleted = i < currentIndex
            const isActive = i === currentIndex
            const isReachable = i <= currentIndex

            return (
              <button
                key={step.key}
                type="button"
                onClick={() => isReachable && onStepClick(step.key)}
                disabled={!isReachable}
                className={`group relative z-10 flex flex-col items-center gap-1.5 ${isReachable ? "cursor-pointer" : ""}`}
              >
                <span
                  className={`flex size-8 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                    isCompleted
                      ? "bg-stone text-slate"
                      : isActive
                        ? "bg-red text-white"
                        : "border border-stone bg-white text-warm-grey/50"
                  }`}
                >
                  {isCompleted ? (
                    <svg className="size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </span>
                <span
                  className={`text-[11px] transition-colors ${
                    isActive
                      ? "font-semibold text-charcoal"
                      : isCompleted
                        ? "font-medium text-slate"
                        : "text-warm-grey/50"
                  }`}
                >
                  {step.label}
                </span>
              </button>
            )
          })}
        </div>
      </nav>

      {/* Mobile progress */}
      <div className="mb-6 lg:hidden">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-charcoal">
            Step {currentIndex + 1} of {STEPS.length}
            <span className="text-warm-grey"> &middot; {STEPS[currentIndex].label}</span>
          </p>
        </div>
        <Progress value={progressPercent} className="h-1.5 bg-stone/30 [&>[data-slot=progress-indicator]]:bg-red" />
      </div>
    </>
  )
}

export { STEPS }
